import gql from "graphql-tag";
import {
  PdpCarouselsResult,
  ProductGender,
  CarouselRecommendTypeInput,
} from "src/types/graphql.d";
import { ApolloError } from "@apollo/client";
import { useQuery } from "@apollo/react-hooks";
import { useFeatureFlags } from "./useFeatureFlags";
import { useAreFeatureFlagsResolved } from "src/hooks/stores/use-feature-flags-store";
import { getConstructorTrackingData } from "src/utils/constructorTrackingData";

interface PdpCarouselsVars {
  slug: string;
  shop_type: ProductGender;
  limit: number;
  carousel_recommend_type?: CarouselRecommendTypeInput;
}

interface PdpCarouselsResponse {
  pdpCarousels: PdpCarouselsResult | undefined;
}

interface PdpCarouselsHook {
  data: PdpCarouselsResponse | undefined;
  loading: boolean;
  error: ApolloError | undefined;
}

const PDP_CAROUSELS_QUERY = gql`
  query pdpCarousels(
    $slug: String!
    $shop_type: ProductGender!
    $limit: Int!
    $carousel_recommend_type: CarouselRecommendTypeInput
  ) {
    pdpCarousels(
      slug: $slug
      shop_type: $shop_type
      limit: $limit
      carousel_recommend_type: $carousel_recommend_type
    ) {
      carousels {
        title
        id
        target_link
        target_type
        products {
          id
          name
          slug
          price
          regular_price
          brand {
            id
            name
            designer_brand
            brand_sale_type
          }
          thumbnail
          labels {
            type
            value
            color
            priority
          }
          available_sizes
          available_sizes_label
          currency {
            code
            symbol
            position
          }
          user_wishlist_item_id
          gate {
            level
          }
          images {
            url
            device
            shop_type
          }
          product_kids_gender
          product_shop_type
        }
        response_source_type
        pod_id
        result_id
        strategy_id
      }
    }
  }
`;

const DEFAULT_LIMIT = 12;
const usePdpCarousels = ({
  slug,
  shopType,
  limit = DEFAULT_LIMIT,
  fetchPolicy = "cache-first",
}: {
  slug: string;
  shopType: ProductGender;
  limit?: number;
  carousel_recommend_type?: CarouselRecommendTypeInput;
  fetchPolicy?: "cache-and-network" | "cache-first";
}): PdpCarouselsHook => {
  const {
    pdpCarouselRecommender,
    pdpTrendingCarouselRecommender,
    recommendationEngineRouting,
  } = useFeatureFlags();
  // The query carries the shopper's Eppo variants, so it waits for them rather
  // than going out with the defaults (which route every shopper to Constructor).
  const areFeatureFlagsResolved = useAreFeatureFlagsResolved();

  const variables = {
    slug,
    shop_type: shopType,
    limit,
    carousel_recommend_type: {
      pdp_similar_items_carousel:
        typeof pdpCarouselRecommender === "string"
          ? pdpCarouselRecommender
          : null,
      pdp_trending_items_carousel:
        typeof pdpTrendingCarouselRecommender === "string"
          ? pdpTrendingCarouselRecommender
          : null,
      recommend_routing:
        typeof recommendationEngineRouting === "string"
          ? recommendationEngineRouting
          : null,
      constructor_tracking_data: getConstructorTrackingData(),
    },
  };

  const { data, loading, error } = useQuery<
    PdpCarouselsResponse,
    PdpCarouselsVars
  >(PDP_CAROUSELS_QUERY, {
    variables,
    fetchPolicy,
    skip: !areFeatureFlagsResolved,
  });

  return {
    data,
    loading: loading || !areFeatureFlagsResolved,
    error,
  };
};

export default usePdpCarousels;
